/**
 * Secure Email Service - Client-side wrapper for Firebase Functions
 * This service calls secure server-side functions instead of using AWS credentials directly
 */

import { httpsCallable } from 'firebase/functions';
import app, { auth, functions } from '../firebase';
import { generateUnsubscribeHeaders, shouldAddUnsubscribeHeaders } from '../utils/emailHeaders';
import { formatDomainWarmingHelpMessage } from '../utils/domainWarming';
import { APP_WEB_ORIGIN } from '../config/domainConfig';
import { applyClickTrackingToHtml } from '../utils/emailLinkRegistry';
import type { CampaignLinkRecord } from '../types/emailLinkAnalytics';

// Email interfaces (matching server-side)
export interface EmailRecipient {
  email: string;
  name?: string;
}

export interface EmailOptions {
  from: EmailRecipient;
  to: EmailRecipient[];
  cc?: EmailRecipient[];
  bcc?: EmailRecipient[];
  subject: string;
  htmlBody?: string;
  textBody?: string;
  replyTo?: string;
  attachments?: EmailAttachment[];
  tags?: { [key: string]: string };
  headers?: { [key: string]: string };
  userId?: string;
  workspaceId?: string;
  unsubscribeUrl?: string;
}

export interface EmailAttachment {
  filename: string;
  content: string; // base64 encoded
  contentType: string;
}

export interface CampaignEmailOptions extends EmailOptions {
  campaignId: string;
  templateId?: string;
  templateData?: { [key: string]: any };
  trackingEnabled?: boolean;
  openTracking?: boolean;
  clickTracking?: boolean;
  /** Pre-built link registry for per-link click analytics (Phase 1). */
  linkRegistry?: CampaignLinkRecord[];
}

export interface EmailResult {
  success: boolean;
  messageId?: string;
  error?: string;
  /** Backend mail path: zeptomail | ses (marketing may differ from transactional). */
  provider?: string | null;
  wasFallback?: boolean;
}

export function applyCampaignTrackingToHtml(
  html: string,
  options: {
    trackingEnabled: boolean;
    openTracking?: boolean;
    clickTracking?: boolean;
    campaignId: string;
    workspaceId: string;
    recipientEmail: string;
    recipientId?: string;
    linkRegistry?: CampaignLinkRecord[];
  }
): string {
  if (!options.trackingEnabled || !html) {
    return html;
  }

  // Always use production app origin so clicks work from inboxes (not localhost).
  const trackingDomain = APP_WEB_ORIGIN;

  let htmlBody = html;
  const trackingParams = new URLSearchParams({
    campaign_id: options.campaignId,
    workspace_id: options.workspaceId,
    recipient_email: options.recipientEmail,
    timestamp: Date.now().toString(),
    event_type: 'open',
  });
  if (options.recipientId) {
    trackingParams.set('recipient_id', options.recipientId);
  }

  if (options.openTracking) {
    // Open tracking must be server-side (email clients don't run SPA JS).
    const trackingPixel = `<img src="${trackingDomain}/api/track/open?${trackingParams.toString()}" width="1" height="1" style="display:none;" alt="" />`;
    if (htmlBody.includes('</body>')) {
      htmlBody = htmlBody.replace('</body>', `${trackingPixel}</body>`);
    } else {
      htmlBody += trackingPixel;
    }
  }

  if (options.clickTracking) {
    htmlBody = applyClickTrackingToHtml(htmlBody, {
      trackingOrigin: trackingDomain,
      campaignId: options.campaignId,
      workspaceId: options.workspaceId,
      recipientEmail: options.recipientEmail,
      recipientId: options.recipientId,
      linkRegistry: options.linkRegistry || [],
    });
  }

  return htmlBody;
}

class SecureEmailService {
  private sendEmailFunction: any;
  private sendBulkEmailsFunction: any;
  private readonly functionsRegion = 'us-central1';

  constructor() {
    // Initialize Firebase Functions
    this.sendEmailFunction = httpsCallable(functions, 'sendEmail');
    this.sendBulkEmailsFunction = httpsCallable(functions, 'sendBulkEmails');
  }

  // Send single email
  async sendEmail(options: EmailOptions): Promise<EmailResult> {
    // Inject unsubscribe headers if this is a marketing email
    const headers = { ...options.headers };
    const mainRecipient = options.to && options.to.length > 0 ? options.to[0].email : '';
    
    if (mainRecipient && shouldAddUnsubscribeHeaders(options.subject, options.tags)) {
      const unsubHeaders = generateUnsubscribeHeaders(
        mainRecipient, 
        options.userId, 
        options.workspaceId, 
        options.unsubscribeUrl
      );
      Object.assign(headers, unsubHeaders);
    }

    const emailData = {
      from: options.from,
      to: options.to,
      cc: options.cc,
      bcc: options.bcc,
      subject: options.subject,
      htmlBody: options.htmlBody,
      textBody: options.textBody,
      replyTo: options.replyTo,
      tags: options.tags,
      headers: headers,
    };

    try {
      const result = await this.sendEmailFunction({ emailData });
      return {
        success: true,
        messageId: result.data.messageId,
        provider: result.data.provider ?? null,
        wasFallback: Boolean(result.data.wasFallback),
      };
    } catch (error: any) {
      console.error('Error sending email via Firebase Functions:', error);

      const shouldAttemptHttpFallback = typeof fetch !== 'undefined' && (
        error?.code === 'functions/internal' ||
        error?.code === 'functions/unavailable' ||
        error?.code === 'functions/not-found' ||
        error?.message?.toLowerCase?.().includes('failed to fetch') ||
        error?.message?.toLowerCase?.().includes('cors') ||
        error?.message?.toLowerCase?.().includes('network')
      );

      if (shouldAttemptHttpFallback) {
        const fallbackResult = await this.sendEmailViaHttp(emailData);
        if (fallbackResult) {
          return fallbackResult;
        }
      }

      // Provide more specific error messages
      let errorMessage = 'Failed to send email';

      if (error.code === 'functions/not-found' || error.message?.includes('not found')) {
        errorMessage = 'Email service is not configured. Please deploy Firebase Functions first.';
      } else if (error.code === 'functions/unauthenticated' || error.message?.includes('unauthenticated')) {
        errorMessage = 'Authentication required to send emails. Please log in and try again.';
      } else if (error.code === 'functions/permission-denied' || error.message?.includes('permission')) {
        errorMessage = 'Permission denied. Please check your account permissions.';
      } else if (error.message) {
        errorMessage = formatDomainWarmingHelpMessage(error.message);
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  // Send campaign email
  async sendCampaignEmail(options: CampaignEmailOptions): Promise<EmailResult> {
    try {
      // Add tracking parameters if enabled
      let htmlBody = options.htmlBody;
      let textBody = options.textBody;

      if (options.trackingEnabled && htmlBody) {
        const recipientEmail = options.to && options.to.length > 0 ? options.to[0].email : '';
        const recipientId = options.tags?.RecipientId || '';
        htmlBody = applyCampaignTrackingToHtml(htmlBody, {
          trackingEnabled: true,
          openTracking: options.openTracking,
          clickTracking: options.clickTracking,
          campaignId: options.campaignId,
          workspaceId: options.workspaceId || '',
          recipientEmail,
          recipientId: recipientId || undefined,
          linkRegistry: options.linkRegistry,
        });
      }

      const emailOptions: EmailOptions = {
        ...options,
        htmlBody,
        textBody,
        tags: {
          ...options.tags,
          CampaignId: options.campaignId,
          Source: 'Scubamail',
        },
        headers: options.headers,
      };

      return await this.sendEmail(emailOptions);
    } catch (error: any) {
      console.error('Error sending campaign email:', error);
      return {
        success: false,
        error: error.message || 'Unknown error occurred',
      };
    }
  }

  // Send bulk emails
  async sendBulkEmails(
    emails: CampaignEmailOptions[]
  ): Promise<{ results: EmailResult[]; successCount: number; failureCount: number }> {
    try {
      console.log('Preparing bulk email data...', {
        emailCount: emails.length,
        firstEmail: emails[0] ? {
          to: emails[0].to,
          subject: emails[0].subject,
          from: emails[0].from
        } : null
      });

      // Prepare email data array
      const emailDataArray = emails.map(email => {
        // Inject unsubscribe headers for each recipient
        const headers = { ...email.headers };
        const recipientEmail = email.to && email.to.length > 0 ? email.to[0].email : '';
        
        if (recipientEmail && shouldAddUnsubscribeHeaders(email.subject, email.tags)) {
          const unsubHeaders = generateUnsubscribeHeaders(
            recipientEmail, 
            email.userId, 
            email.workspaceId, 
            email.unsubscribeUrl
          );
          Object.assign(headers, unsubHeaders);
        }

        return {
          from: email.from,
          to: email.to,
          cc: email.cc,
          bcc: email.bcc,
          subject: email.subject,
          htmlBody: email.htmlBody,
          textBody: email.textBody,
          replyTo: email.replyTo,
          campaignId: email.campaignId,
          tags: email.tags,
          headers: headers,
        };
      });

      console.log('Calling sendBulkEmails Firebase Function...');
      const result = await this.sendBulkEmailsFunction({ emailDataArray });
      console.log('sendBulkEmails Function response:', {
        hasResults: !!result.data?.results,
        resultCount: result.data?.results?.length,
        successCount: result.data?.successCount,
        failureCount: result.data?.failureCount
      });

      return {
        results: result.data.results || [],
        successCount: result.data.successCount || 0,
        failureCount: result.data.failureCount || 0,
      };
    } catch (error: any) {
      console.error('Error sending bulk emails:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });

      // Provide more specific error messages
      let errorMessage = 'Failed to send bulk emails';

      if (error.code === 'functions/not-found' || error.message?.includes('not found')) {
        errorMessage = 'Email service is not configured. Please deploy Firebase Functions first.';
      } else if (error.code === 'functions/unauthenticated' || error.message?.includes('unauthenticated')) {
        errorMessage = 'Authentication required to send emails. Please log in and try again.';
      } else if (error.code === 'functions/permission-denied' || error.message?.includes('permission')) {
        errorMessage = 'Permission denied. Please check your account permissions.';
      } else if (error.message?.includes('subscription') || error.message?.includes('No active subscription')) {
        errorMessage = 'An active subscription is required to send emails. Please upgrade your plan to continue sending campaigns.';
      } else if (error.message) {
        errorMessage = formatDomainWarmingHelpMessage(error.message);
      }

      // Return error for all emails
      const errorResults: EmailResult[] = emails.map(() => ({
        success: false,
        error: errorMessage,
      }));

      return {
        results: errorResults,
        successCount: 0,
        failureCount: emails.length,
      };
    }
  }

  private async sendEmailViaHttp(emailData: EmailOptions): Promise<EmailResult | null> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.warn('⚠️ HTTP fallback skipped: no authenticated user available');
        return null;
      }

      const idToken = await currentUser.getIdToken();
      const projectId = app?.options?.projectId || process.env.REACT_APP_FIREBASE_PROJECT_ID;
      const customFunctionsUrl = process.env.REACT_APP_FIREBASE_FUNCTIONS_URL;
      const baseUrl = customFunctionsUrl
        ? customFunctionsUrl.replace(/\/$/, '')
        : (projectId ? `https://${this.functionsRegion}-${projectId}.cloudfunctions.net` : null);

      if (!baseUrl) {
        console.warn('⚠️ HTTP fallback skipped: unable to determine Firebase Functions URL');
        return null;
      }

      const response = await fetch(`${baseUrl}/sendEmailHttp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ emailData }),
      });

      if (!response.ok) {
        const errorBody = await response.text();
        console.error('❌ HTTP fallback sendEmail failed:', response.status, errorBody);
        
        let details = errorBody;
        try {
          const parsed = JSON.parse(errorBody);
          details = parsed.error || parsed.details || errorBody;
        } catch (e) {
          // Not JSON
        }
        
        return {
          success: false,
          error: `HTTP fallback failed: ${details || response.statusText || 'Unknown error'}`,
        };
      }

      const data = await response.json();
      if (data.success) {
        console.log('✅ HTTP fallback sendEmail succeeded:', data.messageId);
        return {
          success: true,
          messageId: data.messageId,
          provider: data.provider ?? null,
          wasFallback: Boolean(data.wasFallback),
        };
      }

      return {
        success: false,
        error: data.error || 'HTTP fallback failed to send email',
      };
    } catch (fallbackError: any) {
      console.error('❌ HTTP fallback encountered an error:', fallbackError);
      return {
        success: false,
        error: fallbackError?.message || 'HTTP fallback failed',
      };
    }
  }
}

// Export singleton instance
export const secureEmailService = new SecureEmailService();

// Export for backward compatibility
export default secureEmailService;

